import { motion } from "framer-motion";
import {
  Clapperboard,
  Workflow,
  Bot,
  ClipboardCheck,
  Users,
  Globe,
} from "lucide-react";

const SERVICES = [
  {
    icon: Clapperboard,
    title: "AI Commercials & Brand Content",
    body:
      "High-end commercials, brand films, and short-form content — delivered in days, not weeks. AI-native production with Hollywood-level creative direction.",
  },
  {
    icon: Workflow,
    title: "Marketing Automation",
    body:
      "Lead recovery agents, email campaigns, social media closers, and referral systems that run 24/7 without you lifting a finger.",
  },
  {
    icon: Bot,
    title: "AI Agent Systems",
    body:
      "Custom AI agents that handle customer service, sales follow-up, internal operations, and data analysis — tailored to your business.",
  },
  {
    icon: ClipboardCheck,
    title: "AI Assessments",
    body:
      "A comprehensive audit of your business operations. We find the bottlenecks, calculate the ROI, and deliver a step-by-step implementation plan.",
    badge: "Starting at $999",
  },
  {
    icon: Users,
    title: "AI Concierge",
    body:
      "Live implementation sessions with our team. We sit with you, find opportunities, and build AI workflows on the spot. Done with you, not just for you.",
  },
  {
    icon: Globe,
    title: "Websites & Digital Experiences",
    body:
      "Modern, high-converting websites and digital platforms built with AI-assisted design and development.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <span
            className="text-xs uppercase tracking-[0.22em] text-[#00d4ff]/90"
            data-testid="services-eyebrow"
          >
            Our Services
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight"
            data-testid="services-headline"
          >
            What We Build
          </motion.h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-xl">
            Six end-to-end offerings designed to compound your time, your
            content, and your revenue.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.55 }}
                data-testid={`service-card-${i}`}
                className="group relative flex flex-col rounded-xl border border-white/10 bg-[#13131a]/80 p-7 hover:border-[#00d4ff]/30 hover:bg-[#16161f] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#00d4ff]/30 bg-[#00d4ff]/10">
                    <Icon className="h-5 w-5 text-[#00d4ff]" />
                  </span>
                  {s.badge && (
                    <span className="rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/10 px-2.5 py-1 text-[10px] uppercase tracking-wider text-[#9be8ff]">
                      {s.badge}
                    </span>
                  )}
                </div>
                <h3 className="font-display mt-6 text-xl font-semibold text-white">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-zinc-400">
                  {s.body}
                </p>
                <div className="pointer-events-none absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_top,rgba(0,212,255,0.08),transparent_60%)]" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
